import React, { useEffect, useState, useRef } from 'react';
import Label from 'components/v2/forms/Label';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import LocationSearch from 'components/site/location-search';
import Badge from 'components/v2/Badge';
import CustomerSearch from 'components/site/customer-search';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import ChevronRightIcon from 'components/v2/icons/ChevronRightIcon';
import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import ToggleSwitch from 'components/v2/ToggleSwitch';
import { ConfirmActionModal } from 'components/ui/modal';
import { useDeleteShipperWithAssociation } from 'hooks/warehouse/useManageWarehouse';
import { showToast } from 'stores/ToastStore';
import Alert from 'components/ui/Alert';
import Table from 'components/v2/Table';
import { checkExistingWarehouse } from 'utils/WarehouseUserAPIUtils';
import { unescapeSpecialChars } from 'helpers/helper_functions';

function Asterisk() {
  return <span className="text-xs font-medium text-gray-500">*</span>;
}

const WarehouseForm = ({
  data = {},
  saving,
  onValidSuccess,
  onValidFail,
  afterSave,
}) => {
  const [formData, setFormData] = useState({
    shippers: [],
    address: '',
    warehouse_location: '',
    ...data,
  });

  const isFirstRender = useRef(true);

  const [warehouseId, setWarehouseId] = useState('');
  const [showDeleteShipperModal, setShowDeleteShipperModal] = useState(false);
  const [deleteShipperData, setDeleteShipperData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isWarehouseExist, setIsWarehouseExist] = useState(false);

  const { t } = useTranslation();

  const onDeleteShipperSuccess = () => {
    setShowDeleteShipperModal(false);
    showToast(
      t('DELETE_SHIPPER_SUCCESS'),
      t('DELETE_SHIPPER_SUCCESS'),
      'success',
    );
    afterSave();
  };

  const onDeleteShipperFailed = () => {
    showToast(t('DELETE_SHIPPER_FAILED'), t('DELETE_SHIPPER_FAILED'), 'error');
  };
  const { mutation: deleteShipperMutation } = useDeleteShipperWithAssociation(
    onDeleteShipperSuccess,
    onDeleteShipperFailed,
  );

  const getInitialData = (initData) => {
    setFormData({
      shippers: [],
      address: '',
      warehouse_location: '',
      ...initData,
    });

    setWarehouseId(initData._id);
  };

  useEffect(() => {
    if (Object.keys(data).length) {
      if (isFirstRender.current) {
        getInitialData(data);
      }
      isFirstRender.current = false;
    }
  }, [data]);

  const validateData = (newData) => {
    let warehouseUserErrors = [];
    const { relations: currentShippers = [], warehouse_location } = newData;

    if (!warehouse_location) {
      setErrors([t('REQUIRED_WAREHOUSE_LOCATION')]);
      return;
    }
    const { shippers: initShippers = [] } = data;

    if (!currentShippers.length) {
      setErrors([t('REQUIRED_SHIPPER_UNDER_WAREHOUSE')]);
      return;
    }

    const relations = currentShippers.reduce(
      (acc, { shipper: shipperId, shipperName = '', warehouse_users = [] }) => {
        if (!warehouse_users.length) {
          warehouseUserErrors.push(
            t('REQUIRED_USER_UNDER_SHIPPER', { shipper: shipperName }),
          );
          return acc;
        }
        const foundShipper = initShippers.find(
          (initShipper) => initShipper._id === shipperId,
        );

        if (!foundShipper) {
          acc.push({ shipper: shipperId, warehouse_users });
        } else {
          const users = warehouse_users.reduce((accWarehouse, user) => {
            const foundUser = foundShipper.warehouseUsers.find(
              (wuser) => wuser._id === user._id,
            );
            if (!foundUser) {
              accWarehouse.push(user);
            } else if (
              foundUser.status !== user.status ||
              foundUser.active !== user.active
            ) {
              accWarehouse.push(user);
            }
            return accWarehouse;
          }, []);

          if (users.length) {
            acc.push({ shipper: shipperId, warehouse_users: users });
          }
        }
        return acc;
      },
      [],
    );

    if (warehouseUserErrors.length) {
      setErrors(warehouseUserErrors);
      return;
    }
    setErrors([]);
    const payload = { ...newData };

    return payload;
  };

  const renderErrors = () => {
    if (errors.length) {
      onValidFail();
      return (
        <Alert type="error">
          {errors.map((errorMessage) => (
            <p>{errorMessage}</p>
          ))}
        </Alert>
      );
    }
  };

  const saveForm = () => {
    const payload = validateData(getData());
    if (payload) {
      onValidSuccess(payload);
    }
  };

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  const handleLocationSearch = async (location) => {
    const { address = '', _id } = location;
    const result = await checkExistingWarehouse(_id);

    if (result?.success) {
      const isExist = !!result?.exists;
      setIsWarehouseExist(isExist);
      if (address && !isExist) {
        setFormData((prev) => ({ ...prev, address, warehouse_location: _id }));
      }
    }
  };

  const renderViewMore = (open) => {
    return (
      <div className="flex-shrink-0 flex-grow-0">
        <div className={classNames('flex-grow-0 flex-shrink-0 cursor-pointer')}>
          {!open && <ChevronRightIcon className="w-5 h-5" />}
          {open && <ChevronDownIcon className="w-5 h-5" />}
        </div>
      </div>
    );
  };

  function renderShipperName(name, shipperData) {
    const handleDeleteShipper = () => {
      if (shipperData?.isFromApi) {
        setDeleteShipperData(shipperData);
        setShowDeleteShipperModal(true);
      } else {
        setFormData((prev) => {
          const updatedShippers = prev.shippers.filter((shipper) => {
            return shipper._id !== shipperData._id;
          });
          return { ...prev, shippers: updatedShippers };
        });
      }
    };
    return function renderHeading(open) {
      return (
        <div className="w-full flex justify-between items-center">
          <div className="flex items-center space-x-2 relative">
            {renderViewMore(open)}
            <div className="flex items-center space-x-1">
              <h3 className="flex-1">{unescapeSpecialChars(name)}</h3>
            </div>
          </div>

          <DeleteButton
            color="red"
            className="mr-3"
            onClick={() => handleDeleteShipper(shipperData)}
          />
        </div>
      );
    };
  }

  const isDuplicate = (id = '', userList = []) => {
    const foundUser = userList.find((user) => user._id === id);
    if (foundUser) {
      showToast('', t('USER_ALREADY_EXIST'), 'warning');
    }
    return !!foundUser;
  };

  const handleShipperSelect = (shipperName, shipperItem) => {
    if (isDuplicate(shipperItem._id, formData.shippers)) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      shipperName: shipperName,
      shippers: [...prev.shippers, { ...shipperItem, warehouseUsers: [] }],
    }));
  };

  const renderShipperAssociations = () => {
    const handleWarehouseUserSelect = (shipperId, warehouseItem) => {
      setFormData((prev) => {
        const updatedShippers = prev.shippers.map((shipper) => {
          if (shipper._id === shipperId) {
            if (!isDuplicate(warehouseItem._id, shipper.warehouseUsers)) {
              shipper.warehouseUsers = [
                ...shipper.warehouseUsers,
                { ...warehouseItem, status: false, active: true },
              ];
            }
          }
          return shipper;
        });
        return { ...prev, shippers: updatedShippers };
      });
    };

    const handleWarehouseUserStatusToggle = (
      val,
      shipperId,
      warehouseUserId,
    ) => {
      setFormData((prev) => {
        const shippers = prev.shippers.map((shipper) => {
          if (shipper._id === shipperId) {
            const updatedWarehouseUsers = shipper.warehouseUsers.map((user) => {
              if (user._id === warehouseUserId) {
                user.status = val;
              }
              return user;
            });
            return { ...shipper, warehouseUsers: updatedWarehouseUsers };
          }
          return shipper;
        });
        return { ...prev, shippers };
      });
    };

    const handleDeleteWarehouseUser = (shipperId, warehouseUserId) => {
      setFormData((prev) => {
        const updatedShippers = prev.shippers.map((shipper) => {
          if (shipper._id === shipperId) {
            const updatedWarehouseUsers = shipper.warehouseUsers.reduce(
              (acc, warehouseUser) => {
                if (warehouseUser?.isFromApi) {
                  if (warehouseUser._id === warehouseUserId) {
                    warehouseUser.active = false;
                  }
                  acc.push(warehouseUser);
                } else if (warehouseUserId !== warehouseUser._id) {
                  acc.push(warehouseUser);
                }
                return acc;
              },
              [],
            );

            return { ...shipper, warehouseUsers: updatedWarehouseUsers };
          }
          return shipper;
        });
        return { ...prev, shippers: updatedShippers };
      });
    };

    if (formData?.shippers?.length) {
      return formData.shippers.map(({ warehouseUsers, ...shipper }) => (
        <CollapsibleItem
          renderTitle={renderShipperName(
            shipper.company_name ||
              `${shipper.first_name} ${shipper.last_name}`,
            shipper,
          )}
          className="flex-1 my-4"
          hideIcon
          defaultOpen
        >
          <div
            data-testid="manage-warehouse-form-warehouse-user"
            className="mt-4 w-full"
          >
            <Table
              headers={[
                { label: t('WAREHOUSE_USER') },
                { label: t('STATUS') },
                { label: '' },
              ]}
              columns={[
                {
                  field: 'name',
                  render: (value, cell) =>
                    cell.active
                      ? value || `${cell.first_name} ${cell.last_name}`
                      : null,
                },
                {
                  field: 'status',
                  render: (value, warehouseUser) =>
                    warehouseUser.active ? (
                      <ToggleSwitch
                        className="-mt-2"
                        enabled={value}
                        onChange={(val) =>
                          handleWarehouseUserStatusToggle(
                            val,
                            shipper._id,
                            warehouseUser._id,
                          )
                        }
                      />
                    ) : null,
                },
                {
                  render: (_, warehouseUser) =>
                    warehouseUser.active ? (
                      <DeleteButton
                        className="float-right"
                        onClick={() =>
                          handleDeleteWarehouseUser(
                            shipper._id,
                            warehouseUser._id,
                          )
                        }
                      />
                    ) : null,
                },
              ]}
              data={warehouseUsers}
              footer={() => (
                <div className="m-2">
                  <CustomerSearch
                    clearOnSelect
                    clearOnFocus
                    showEmptyMessage
                    placeholder={t('SEARCH_ADD_WAREHOUSE_USER')}
                    type="warehouses"
                    onSelect={(_, val) =>
                      handleWarehouseUserSelect(shipper._id, val)
                    }
                  />
                </div>
              )}
            />
          </div>
        </CollapsibleItem>
      ));
    }
  };

  const getData = () => {
    const {
      warehouse_location,
      _id: warehouse_id = '',
      shippers = [],
    } = formData;
    const relations = shippers.map(
      ({ _id, company_name: name, warehouseUsers = [] }) => {
        const warehouse_users = warehouseUsers.map(
          ({ _id, status, active = true, relation_id = '' }) => ({
            _id,
            active,
            status,
            relation_id,
          }),
        );
        return { shipper: _id, warehouse_users, shipperName: name };
      },
    );
    const payload = { warehouse_location, relations };

    if (
      data.warehouse_location &&
      formData.warehouse_location !== data.warehouse_location
    ) {
      payload.warehouse_id = warehouse_id;
    }
    return payload;
  };

  const handleDeleteShipperApi = async () => {
    setUpdating(true);
    await deleteShipperMutation.mutateAsync({
      warehouseId,
      shipperId: deleteShipperData._id,
    });
    setUpdating(false);
  };

  return (
    <>
      {renderErrors()}
      <ConfirmActionModal
        title={t('DELETE_SHIPPER')}
        isOpen={showDeleteShipperModal}
        onClose={() => setShowDeleteShipperModal(false)}
        onConfirm={handleDeleteShipperApi}
        confirmActionColor="red"
        confirmActionText={t('DELETE')}
        actionDisabled={updating}
      >
        {t('DELETE_SHIPPER_MSG')}
      </ConfirmActionModal>
      <div
        data-testid="manage-warehouse-form-location-container"
        className="sm:w-80 mb-6"
      >
        <Label htmlFor="warehouse-location-search" className="mb-2">
          {t('ADDRESS')} <Asterisk />
        </Label>
        <LocationSearch
          key="manage-warehouse-location-search"
          defaultValue={formData?.address}
          placeholder={t('SEARCH_LOCATION_ADD')}
          handleLocationSearch={handleLocationSearch}
          showEmptyMessage
          emptyMessage={t('NO_LOCATION_RESULTS')}
        />

        {formData?.address && (
          <Badge
            withRemove
            onRemove={() => {
              setFormData((prev) => ({
                ...prev,
                address: '',
                warehouse_location: '',
                locationDeleted: true,
              }));
            }}
            className="mt-2"
          >
            {formData.address}
          </Badge>
        )}
      </div>
      {isWarehouseExist && (
        <Alert canClose onClose={() => setIsWarehouseExist(false)} type="error">
          {t('WAREHOUSE_ALREADY_EXIST')}
        </Alert>
      )}
      {!isWarehouseExist && formData?.address && (
        <>
          <hr className="my-6" />
          <div
            data-testid="manage-warehouse-form-shipper-container"
            className="mt-6 flex justiy-end items-end space-x-2 sm:w-80 w-auto"
          >
            <div className="flex-1">
              <Label className="mb-1">{t('SHIPPER')}</Label>
              <CustomerSearch
                clear
                clearOnSelect
                clearOnFocus
                showEmptyMessage
                type="shippers"
                placeholder={t('SEARCH_ADD_SHIPPER')}
                onSelect={(shipperName, shipperItem) => {
                  handleShipperSelect(shipperName, shipperItem);
                }}
              />
            </div>
          </div>
        </>
      )}
      {renderShipperAssociations()}
    </>
  );
};

export default WarehouseForm;
